import { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Input, Typography, Space, Tag, Checkbox, Alert, Spin } from 'antd';
import { useInviteEmailTemplate } from '../../hooks/use_invite_email_template';

const { Text, Paragraph } = Typography;

/**
 * Superadmin-facing compose step for "Send email invite" on an unclaimed
 * person. Pre-fills this node's stored wording (Node account → Invite email)
 * and lets the admin adjust it for this one recipient, optionally saving the
 * edit back as the node default.
 *
 * Presentational on purpose: the caller (Entity.jsx) already owns the
 * claim-link POST, so this hands back {subject, body, saveAsDefault} rather
 * than calling the API itself.
 */

// Preview only. Deliberately the same dumb str.replace as
// services/node_settings.render_invite_email, so a stray brace or an unknown
// {token} previews exactly as it will send. The email that actually goes out
// is always rendered server-side — nothing here decides its contents.
function substitute(text, values) {
    return Object.entries(values).reduce(
        (out, [key, value]) => out.split(`{${key}}`).join(value),
        text || '',
    );
}

function InviteEmailModal({ open, entity, expiresDays = 7, sending, onCancel, onSend }) {
    const { subject, body, placeholders, loading } = useInviteEmailTemplate();
    const [form] = Form.useForm();
    const [saveAsDefault, setSaveAsDefault] = useState(false);
    const [draft, setDraft] = useState({ subject: '', body: '' });

    // Re-seed from the node template each time the modal opens, so an edit
    // abandoned on a previous recipient never leaks into the next invite.
    useEffect(() => {
        if (!open || loading) return;
        form.setFieldsValue({ subject, body });
        setDraft({ subject, body });
        setSaveAsDefault(false);
    }, [open, loading, subject, body, form]);

    const previewValues = useMemo(() => ({
        name: entity?.name || 'their name',
        invite_url: `${window.location.origin}/claim/…`,
        expires_days: String(expiresDays),
    }), [entity?.name, expiresDays]);

    const preview = useMemo(() => ({
        subject: substitute(draft.subject, previewValues),
        body: substitute(draft.body, previewValues),
    }), [draft, previewValues]);

    const handleOk = () => form.validateFields().then(values => onSend({
        subject: values.subject,
        body: values.body,
        saveAsDefault,
    }));

    return (
        <Modal
            open={open}
            title={`Send email invite — ${entity?.name || ''}`}
            okText="Send invite"
            onOk={handleOk}
            onCancel={onCancel}
            confirmLoading={sending}
            width={640}
            destroyOnHidden
        >
            {loading ? (
                <Spin />
            ) : (
                <>
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={`Sending to ${entity?.email}`}
                        description="Edits apply to this invite only, unless you tick the box below."
                    />

                    <Form form={form} layout="vertical" onValuesChange={(_, all) => setDraft(all)}>
                        <Form.Item
                            name="subject"
                            label="Subject"
                            rules={[{ required: true, message: 'Give the email a subject' }]}
                        >
                            <Input />
                        </Form.Item>

                        <Form.Item
                            name="body"
                            label="Message"
                            rules={[{ required: true, message: 'Write a message' }]}
                            extra={
                                <Space size={4} wrap style={{ marginTop: 4 }}>
                                    <Text type="secondary">Placeholders:</Text>
                                    {placeholders.map(p => (
                                        <Tag key={p} style={{ marginInlineEnd: 0 }}>{`{${p}}`}</Tag>
                                    ))}
                                </Space>
                            }
                        >
                            <Input.TextArea rows={8} />
                        </Form.Item>

                        <Checkbox
                            checked={saveAsDefault}
                            onChange={e => setSaveAsDefault(e.target.checked)}
                        >
                            Also save this as the node&apos;s default invite wording
                        </Checkbox>
                    </Form>

                    <div className="invite-email-preview">
                        <Text type="secondary" className="entity-section-label">Preview</Text>
                        <Paragraph strong style={{ margin: '8px 0 4px' }}>{preview.subject}</Paragraph>
                        <Paragraph type="secondary" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                            {preview.body}
                        </Paragraph>
                    </div>
                </>
            )}
        </Modal>
    );
}

export default InviteEmailModal;
